/**
 * Pure H · Data Mode context.
 *
 * Reads/writes the workspace-level Data Mode flag (demo | live) that
 * drives the persistent DEMO banner, inline KPI chips, and the
 * Presentation Mode / Investor Room hard-lock.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

export type DataMode = "demo" | "live";

type Ctx = {
  dataMode: DataMode;
  loading:  boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  refresh:   () => Promise<void>;
  setDataMode: (m: DataMode) => Promise<void>;
};

const DataModeContext = createContext<Ctx>({
  dataMode: "demo", loading: true, updatedAt: null, updatedBy: null,
  refresh: async () => {}, setDataMode: async () => {},
});

export function DataModeProvider({ children }: { children: React.ReactNode }) {
  const [dataMode, setDm] = useState<DataMode>("demo");
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);

  const refresh = useMemo(() => async () => {
    try {
      const r = await fetch(`${API}/system/data-mode`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      // Only mark "demo" when the server EXPLICITLY says so. On network /
      // auth errors we default to "live" so we never falsely lite up the
      // DEMO banner across a truly empty workspace and mislead the operator.
      setDm(d.dataMode === "demo" ? "demo" : "live");
      setUpdatedAt(d.updatedAt || null);
      setUpdatedBy(d.updatedBy || null);
    } catch {
      // Fetch failed (network / auth). Do NOT flip to demo — that would
      // paint a false DEMO banner across a live workspace.
      setDm("live");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setDataMode = async (m: DataMode) => {
    const r = await fetch(`${API}/system/data-mode`, {
      method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dataMode: m }),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
    await refresh();
  };

  const value: Ctx = { dataMode, loading, updatedAt, updatedBy, refresh, setDataMode };
  return <DataModeContext.Provider value={value}>{children}</DataModeContext.Provider>;
}

export const useDataMode = () => useContext(DataModeContext);

/** Persistent, non-dismissible banner that renders on every route when demo. */
export function DemoBanner() {
  const { dataMode, loading } = useDataMode();
  if (loading || dataMode !== "demo") return null;
  return (
    <div
      data-testid="demo-data-banner"
      role="alert"
      style={{
        position: "sticky", top: 0, zIndex: 60,
        background: "#fef3c7", color: "#78350f",
        borderBottom: "2px solid #b45309",
        padding: "8px 20px",
        fontSize: "13px", fontWeight: 600, textAlign: "center",
        letterSpacing: "0.02em",
      }}
    >
      ⚠ DEMO DATA — these figures are simulated and are not ORVAN&apos;s real business data.
    </div>
  );
}

/** Inline chip rendered next to any KPI value derived from seeded collections. */
export function DemoChip({ testId }: { testId?: string }) {
  const { dataMode } = useDataMode();
  if (dataMode !== "demo") return null;
  return (
    <span
      data-testid={testId || "demo-chip"}
      title="This value is derived from demo seed data"
      style={{
        display: "inline-block",
        marginLeft: 6, padding: "1px 6px",
        borderRadius: 3, fontSize: "9px", fontWeight: 700,
        letterSpacing: "0.14em", textTransform: "uppercase",
        background: "#fde68a", color: "#78350f",
        verticalAlign: "middle",
      }}
    >
      DEMO
    </span>
  );
}
